import { asNullableRecord } from "@openclaw/normalization-core/record-coerce";
import { normalizeOptionalString as normalizedString } from "@openclaw/normalization-core/string-coerce";
import { pruneMapToMaxSize } from "../../../../src/infra/map-size.ts";
import type { GatewaySessionRow, SessionsListResult } from "../../api/types.ts";
import { projectSessionResultRows } from "./reconcile.ts";

// Lifecycle notes are transient UI state, so bound them for long-lived board tabs.
const MAX_TRACKED_SWARM_GROUPS = 10_000;
// Completed members stay visible while any group child is active, so retain the
// supported lifetime membership ceiling rather than only the live-child cap.
const MAX_TRACKED_SWARM_CHILDREN = 100_000;

function setBounded<K, V>(map: Map<K, V>, key: K, value: V, limit: number): void {
  map.delete(key);
  map.set(key, value);
  pruneMapToMaxSize(map, limit);
}

/** Tracks transient, group-scoped Swarm notes across canonical session-list refreshes. */
export class SwarmActivityTracker {
  private readonly currentPhaseByGroup = new Map<string, string>();
  // First-observation rank per "<groupId>\u0000<phase>": buckets render in the
  // order phases were announced, not in canonical session-list row order.
  private readonly phaseRankByGroupPhase = new Map<string, number>();
  private nextPhaseRank = 0;
  private readonly latestLogByGroup = new Map<string, string>();
  private readonly phaseByChild = new Map<string, string>();

  clear(): void {
    this.currentPhaseByGroup.clear();
    this.phaseRankByGroupPhase.clear();
    this.nextPhaseRank = 0;
    this.latestLogByGroup.clear();
    this.phaseByChild.clear();
  }

  observe(payload: unknown): boolean {
    const event = asNullableRecord(payload);
    if (!event) {
      return false;
    }
    const source = asNullableRecord(event.session) ?? event;
    const groupId = normalizedString(event.swarmGroupId) ?? normalizedString(source.swarmGroupId);
    if (!groupId) {
      return false;
    }
    const kind = normalizedString(event.kind);
    const text = normalizedString(event.text);
    if ((kind === "phase" || kind === "log") && text) {
      if (kind === "phase") {
        const rankKey = `${groupId}\u0000${text}`;
        if (!this.phaseRankByGroupPhase.has(rankKey)) {
          setBounded(
            this.phaseRankByGroupPhase,
            rankKey,
            this.nextPhaseRank,
            MAX_TRACKED_SWARM_CHILDREN,
          );
          this.nextPhaseRank += 1;
        }
      }
      setBounded(
        kind === "phase" ? this.currentPhaseByGroup : this.latestLogByGroup,
        groupId,
        text,
        MAX_TRACKED_SWARM_GROUPS,
      );
      return true;
    }

    const childKey = normalizedString(source.key) ?? normalizedString(event.sessionKey);
    if (!childKey) {
      return true;
    }
    const explicitPhase = normalizedString(source.swarmPhase) ?? normalizedString(event.swarmPhase);
    if (explicitPhase) {
      setBounded(this.phaseByChild, childKey, explicitPhase, MAX_TRACKED_SWARM_CHILDREN);
      return true;
    }
    // Implicit phase assignment is a creation-time fact: only a child ADMITTED
    // after phase('X') belongs to X. Status/completion updates for a child that
    // predates any phase note must never retro-stamp it out of Unphased.
    if (normalizedString(event.reason) === "create" && !this.phaseByChild.has(childKey)) {
      const currentPhase = this.currentPhaseByGroup.get(groupId);
      if (currentPhase) {
        setBounded(this.phaseByChild, childKey, currentPhase, MAX_TRACKED_SWARM_CHILDREN);
      }
    }
    return true;
  }

  decorate(result: SessionsListResult | null): SessionsListResult | null {
    if (!result) {
      return result;
    }
    const sessions = result.sessions.map((row): GatewaySessionRow => {
      const phase = this.phaseByChild.get(row.key) ?? row.swarmPhase;
      const groupId = row.swarmGroupId?.trim();
      const log = (groupId ? this.latestLogByGroup.get(groupId) : undefined) ?? row.swarmLog;
      const phaseRank =
        (phase && groupId
          ? this.phaseRankByGroupPhase.get(`${groupId}\u0000${phase}`)
          : undefined) ?? row.swarmPhaseRank;
      if (phase === row.swarmPhase && log === row.swarmLog && phaseRank === row.swarmPhaseRank) {
        return row;
      }
      return {
        ...row,
        ...(phase ? { swarmPhase: phase } : {}),
        ...(phaseRank !== undefined ? { swarmPhaseRank: phaseRank } : {}),
        ...(log ? { swarmLog: log } : {}),
      };
    });
    return projectSessionResultRows(result, sessions);
  }
}
